/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type CSSProperties,
	type MutableRefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useMotion } from '@atlaskit/motion/entering/use-motion';
import {
	type contentHeightWhenFixed,
	type contentInsetBlockStart,
	mainMinimumWidthVar,
	type UNSAFE_LayoutAreaMode,
	type UNSAFE_LayoutAreaState,
	UNSAFE_resolveLayoutWidth,
} from '@atlaskit/navigation-system/layout/layout-sizing';
import {
	PanelSplitter,
	PanelSplitterProvider,
	type ResizeBounds,
} from '@atlaskit/navigation-system/layout/panel-splitter';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { localPanelSplitterId } from './constants';

const localPanelVar = '--p_lclPnlW';
const localPanelOverlayWidthVar = '--p_lclPnlOverlayW';

const panelSplitterResizingVar = '--p_lclPnlRsz';
const inlineMediaQuery = '(min-width: 60rem) and (max-width: 63.999rem), (min-width: 75rem)';
const mainMinimumWidth = 320;

function getPixelCustomProperty(
	element: HTMLElement,
	propertyName: string,
	fallback: number,
): number {
	const raw = getComputedStyle(element).getPropertyValue(propertyName).trim();
	const value = Number.parseFloat(raw);
	return Number.isFinite(value)
		? UNSAFE_resolveLayoutWidth(raw.endsWith('vw') ? `${value}vw` : value, window.innerWidth)
		: fallback;
}

const styles = cssMap({
	panelWrapper: {
		position: 'sticky',
		backgroundColor: token('elevation.surface.overlay'),
		boxSizing: 'border-box',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		height:
			'calc(100vh - var(--n_bnrM, 0px) - var(--n_tNvM, 0px))' satisfies typeof contentHeightWhenFixed,
		insetBlockStart: '0px',

		justifySelf: 'end',
		gridArea: 'main',
		/**
		 * For mobile viewports, the panel will try to take the minimum width, but no larger than 90% of the screen width.
		 *
		 * The minimum width is derived from the default width of the panel.
		 *
		 * This ensures the panel:
		 *
		 * - only shrinks below its minimum width if the viewport is too small
		 * - is not forced to grow if it is smaller than `400px`
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		width: `min(90%, var(${panelSplitterResizingVar}, var(${localPanelOverlayWidthVar})))`,
		'@media (min-width: 60rem)': {
			gridArea: 'panel',
			backgroundColor: token('elevation.surface'),
			// Styles do not apply without the nested selector.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				width: 'var(--minWidth)',
			},
		},
		'@media (min-width: 64rem)': {
			gridArea: 'main',
			backgroundColor: token('elevation.surface.overlay'),
			// This sets the sticky point to be just below top bar + banner. It's needed to ensure the stick
			// point is exactly where this element is rendered to with no wiggle room. Unfortunately the CSS
			// spec for sticky doesn't support "stick to where I'm initially rendered" so we need to tell it.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			insetBlockStart:
				'calc(var(--n_bnrM, 0px) + var(--n_tNvM, 0px))' satisfies typeof contentInsetBlockStart,
			// Styles do not apply without the nested selector.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
				width: `min(90%, var(${panelSplitterResizingVar}, var(${localPanelOverlayWidthVar})))`,
			},
		},
		'@media (min-width: 75rem)': {
			gridArea: 'panel',
			backgroundColor: token('elevation.surface'),
			// Styles do not apply without the nested selector.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				width: 'var(--minWidth)',
			},
		},
		'@media (min-width: 80rem)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				width:
					'min(var(--p_lclPnlRsz, var(--p_lclPnlW)), calc(var(--minWidth) + ((100vw - 80rem) / 2)), max(var(--minWidth), calc(100cqi - 20rem)))',
			},
		},
		'@media (min-width: 90rem)': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
			'&&': {
				width:
					'clamp(var(--minWidth), var(--p_lclPnlRsz, var(--p_lclPnlW)), max(var(--minWidth), calc(100cqi - 20rem)))',
			},
		},
	},
	managedInline: {
		// The legacy responsive width rules below use `&&`. Managed sizing must win at
		// those same breakpoints so the panel content follows its live grid track.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&&': {
			gridArea: 'panel',
			backgroundColor: token('elevation.surface'),
			insetBlockStart: '0px',
			width: `var(${panelSplitterResizingVar}, var(${localPanelVar}))`,
		},
	},
	managedOverlay: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&&': {
			gridArea: 'main',
			backgroundColor: token('elevation.surface.overlay'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			insetBlockStart:
				'calc(var(--n_bnrM, 0px) + var(--n_tNvM, 0px))' satisfies typeof contentInsetBlockStart,
			width: `var(${panelSplitterResizingVar}, var(${localPanelOverlayWidthVar}))`,
		},
	},
	panelScrollContainer: {
		overflow: 'auto',
		height: '100%',
	},
	border: {
		// We use a shadow when the panel is an overlay
		boxShadow: token('elevation.shadow.overlay'),
		// Not required, but declaring explicitly because we really don't want a border at small sizes
		// Previously we had a transparent border to maintain width, but this unintentionally acted as padding
		borderInlineStart: 'none',
		'@media (min-width: 60rem)': {
			boxShadow: 'initial',
			borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		},
		'@media (min-width: 64rem)': {
			boxShadow: token('elevation.shadow.overlay'),
			borderInlineStart: 'none',
		},
		'@media (min-width: 75rem)': {
			// Hiding the box shadow because we are adding a border
			boxShadow: 'initial',
			// We only want the border to be visible when it is not an overlay
			borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		},
	},
	managedInlineBorder: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			boxShadow: 'initial',
			borderInlineStart: `${token('border.width')} solid ${token('color.border')}`,
		},
	},
	managedOverlayBorder: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			boxShadow: token('elevation.shadow.overlay'),
			borderInlineStart: 'none',
		},
	},
	entering: {
		'@media (prefers-reduced-motion: no-preference)': {
			animation: token('motion.panel.enter.right'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			"[dir='rtl'] &": {
				animation: token('motion.panel.enter.left'),
			},
			transitionProperty: 'position',
			transitionDuration: token('motion.duration.instant'),
			transitionDelay: token('motion.duration.short'),
			transitionBehavior: 'allow-discrete',
			'@starting-style': {
				position: 'fixed',
			},
		},
	},
	exiting: {
		'@media (prefers-reduced-motion: no-preference)': {
			animation: token('motion.panel.exit.right'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			"[dir='rtl'] &": {
				animation: token('motion.panel.exit.left'),
			},
			position: 'fixed',
		},
	},
	contentEntering: {
		animation: token('motion.panel.content.enter'),
	},
	contentExiting: {
		animation: token('motion.panel.content.exit'),
	},
});

const fallbackDefaultWidth = 400;
/**
 * We typically use the `defaultWidth` as the minimum resizing width,
 * but for large default widths we fallback to a standard value.
 *
 * This standard value of `320px` aligns with the responsive layout minimum.
 */
const fallbackResizeMinWidth = 320;

type PanelProps = {
	/**
	 * The content of the layout area.
	 */
	children: React.ReactNode;
	/**
	 * The accessible name of the slot, announced by screen readers.
	 */
	label?: string;
	/**
	 * The default width of the layout area.
	 *
	 * It should be an integer between the resize bounds - the minimum is 120px and the maximum is 50% of the viewport width.
	 *
	 * This value is also used as the minimum resizing width when it is below `320px`.
	 * For larger defaults, `320px` is used as the minimum resizing width.
	 */
	defaultWidth?: number;
	/**
	 * Default width at compact viewport sizes when the app has not supplied an override.
	 */
	compactDefaultWidth?: number;
	/**
	 * Minimum width used by the layout allocation system.
	 */
	minWidth?: number;
	/**
	 * Optional application-defined maximum width.
	 */
	maxWidth?: number;
	/**
	 * The previously committed inline width. Overlay widths intentionally start from defaultWidth.
	 */
	initialInlineWidth?: number;
	width?: number;
	onWidthChange?: (width: number) => void;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Called when the user finishes resizing the panel.
	 */
	onResizeEnd?: (finalWidth: number) => void;
	onInlineResizeEnd?: (finalWidth: number) => void;
	/**
	 * The layout element whose panel grid track follows the live splitter width.
	 */
	layoutRef?: MutableRefObject<HTMLDivElement | null>;
	managedSizing?: UNSAFE_LayoutAreaState;
	getManagedResizeBounds?: () => ResizeBounds | undefined;
	onManagedResizeStart?: (mode: UNSAFE_LayoutAreaMode) => void;
	onManagedResize?: (mode: UNSAFE_LayoutAreaMode, width: number) => void;
	onManagedResizeEnd?: (mode: UNSAFE_LayoutAreaMode, width: number) => void;
};
export const PanelSlot = ({
	children,
	defaultWidth = fallbackDefaultWidth,
	compactDefaultWidth = fallbackResizeMinWidth,
	minWidth = fallbackResizeMinWidth,
	maxWidth,
	initialInlineWidth = defaultWidth,
	width: controlledWidth,
	onWidthChange,
	label = 'Panel',
	testId,
	onResizeEnd,
	onInlineResizeEnd,
	layoutRef,
	managedSizing,
	getManagedResizeBounds,
	onManagedResizeStart,
	onManagedResize,
	onManagedResizeEnd,
}: PanelProps): JSX.Element => {
	const ref = useRef<HTMLDivElement | null>(null);
	const [inlineWidth, setInlineWidth] = useState(initialInlineWidth);
	const [overlayWidth, setOverlayWidth] = useState(compactDefaultWidth);
	const [isInline, setIsInline] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(inlineMediaQuery);
		setIsInline(mediaQuery.matches);

		const onChange = (event: MediaQueryListEvent) => {
			setIsInline(event.matches);
		};

		return bind(mediaQuery, { type: 'change', listener: onChange });
	}, []);

	// useMotion is applied only when the `platform-dst-motion-uplift-panel` gate is enabled.
	const { state, ref: motionRef } = useMotion<HTMLElement>();
	const isMotionUpliftEnabled = fg('platform-dst-motion-uplift-panel');

	// `mergeRefs` returns a new callback ref on every call, and combined with a new array literal
	// this would cause React to detach/re-attach the ref on every render. Memoise it so the merged
	// ref is stable across renders.
	const mergedRef = useMemo(
		() => (isMotionUpliftEnabled ? mergeRefs([ref, motionRef]) : ref),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isMotionUpliftEnabled, motionRef],
	);

	/**
	 * The minimum width that the panel can be resized to by the user.
	 *
	 * We only use the `defaultWidth` up to a point to avoid huge panels.
	 */
	const mode = managedSizing?.mode ?? (isInline ? 'inline' : 'overlay');
	const renderedWidth =
		managedSizing?.width ?? controlledWidth ?? (mode === 'inline' ? inlineWidth : overlayWidth);

	/**
	 * Returns the bounds for resizing, evaluated lazily when needed.
	 *
	 * Defined separately to the slot bounds because the resizing bounds need to be resolvable to a pixel value,
	 * and the panel's slot bounds use a complex CSS expression.
	 */
	const getResizeBounds = useCallback((): ResizeBounds => {
		const managedBounds = getManagedResizeBounds?.();
		if (managedBounds) {
			return managedBounds;
		}
		const panel = ref.current;
		const main = panel?.closest<HTMLElement>('[data-layout-slot][role="main"]');
		const mainWidth = main?.getBoundingClientRect().width ?? 0;
		const isPanelInline = mode === 'inline';
		let layoutMaxWidth = Math.max(minWidth, Math.floor(mainWidth * 0.9));
		if (isPanelInline && panel && main) {
			const panelWidth = panel.getBoundingClientRect().width;
			const effectiveMainMinWidth = getPixelCustomProperty(
				main,
				mainMinimumWidthVar,
				mainMinimumWidth,
			);
			const mainContentWidth = mainWidth - panelWidth;
			const availableWidth = Math.max(0, mainContentWidth - effectiveMainMinWidth);
			layoutMaxWidth = Math.max(minWidth, Math.floor(panelWidth + availableWidth));
		}
		const constrainedMaxWidth = maxWidth ? Math.min(maxWidth, layoutMaxWidth) : layoutMaxWidth;

		return { min: `${minWidth}px`, max: `${Math.max(minWidth, constrainedMaxWidth)}px` };
	}, [getManagedResizeBounds, maxWidth, minWidth, mode]);

	const configuredMaxWidth = maxWidth !== undefined ? `${maxWidth}px` : '100vw';
	const panelVariableWidth = managedSizing
		? `${renderedWidth}px`
		: `clamp(${minWidth}px, ${controlledWidth ?? inlineWidth}px, ${configuredMaxWidth})`;
	const panelOverlayVariableWidth = managedSizing
		? `${renderedWidth}px`
		: `clamp(${minWidth}px, ${controlledWidth ?? overlayWidth}px, min(90vw, ${configuredMaxWidth}))`;
	const onCompleteResize = useCallback(
		(finalWidth: number) => {
			if (mode === 'inline') {
				setInlineWidth(finalWidth);
				onInlineResizeEnd?.(finalWidth);
			} else {
				setOverlayWidth(finalWidth);
			}
			onManagedResizeEnd?.(mode, finalWidth);
			onWidthChange?.(finalWidth);
		},
		[mode, onInlineResizeEnd, onManagedResizeEnd, onWidthChange],
	);
	const onResizeStartInternal = useCallback(() => {
		onManagedResizeStart?.(mode);
	}, [mode, onManagedResizeStart]);
	const onResizeInternal = useCallback(
		(width: number) => onManagedResize?.(mode, width),
		[mode, onManagedResize],
	);
	const handleResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => onResizeEnd?.(finalWidth),
		[onResizeEnd],
	);

	return (
		<section
			aria-label={label}
			css={[
				isMotionUpliftEnabled && state === 'entering' && styles.entering,
				isMotionUpliftEnabled && state === 'exiting' && styles.exiting,
				styles.panelWrapper,
				styles.border,
				managedSizing?.mode === 'inline' && styles.managedInline,
				managedSizing?.mode === 'overlay' && styles.managedOverlay,
				managedSizing?.mode === 'inline' && styles.managedInlineBorder,
				managedSizing?.mode === 'overlay' && styles.managedOverlayBorder,
			]}
			data-testid={testId}
			data-layout-with-panel-slot=""
			style={
				{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
					[localPanelVar]: panelVariableWidth,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values
					[localPanelOverlayWidthVar]: panelOverlayVariableWidth,
					'--minWidth': `${minWidth}px`,
				} as CSSProperties
			}
			ref={mergedRef}
		>
			<PanelSplitterProvider
				panelId={localPanelSplitterId}
				panelRef={ref}
				resizingElementRef={layoutRef}
				panelWidth={renderedWidth}
				onCompleteResize={onCompleteResize}
				onResizeStartInternal={managedSizing ? onResizeStartInternal : undefined}
				onResizeInternal={managedSizing ? onResizeInternal : undefined}
				getResizeBounds={getResizeBounds}
				resizingCssVar={panelSplitterResizingVar}
				position="start"
			>
				{/**
				 * Overflow scroll styles are added here rather than on the `section` container element, so that the panel splitter
				 * component can overflow out of the `Panel` container, to increase the interactive grab area
				 */}
				<div
					css={[
						styles.panelScrollContainer,
						state === 'entering' && styles.contentEntering,
						state === 'exiting' && styles.contentExiting,
					]}
				>
					{children}
					<PanelSplitter
						label="Resize panel"
						testId={testId && `${testId}--splitter`}
						onResizeEnd={onResizeEnd ? handleResizeEnd : undefined}
					/>
				</div>
			</PanelSplitterProvider>
		</section>
	);
};
